import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Chip, Icon } from '@/components';

type TableFilterDropdownProps = {
  column?: any;
  title?: string;
  options?: any;
  value?: string[];
  onChange?: (value: string[]) => void;
  children?: React.ReactNode;
  onClear?: () => void;
  getBadgeLabel?: (values: string[]) => string | null;
  popoverFilter?: boolean;
};

export function TableFilterDropdown({
  column,
  title,
  options,
  value,
  onChange,
  children,
  onClear,
  getBadgeLabel,
  popoverFilter,
}: TableFilterDropdownProps) {
  const normalize = (v: any): string[] => {
    if (!v) return [];
    if (Array.isArray(v)) {
      const flattened = v.flat(Infinity);
      return [
        ...new Set(
          flattened
            .filter((x) => x != null)
            .map((s) => String(s).trim())
            .filter(Boolean)
        ),
      ];
    }
    if (typeof v === 'string')
      return v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    return [];
  };

  const rawValue = value ?? column?.getFilterValue();
  const selectedValues: string[] = normalize(rawValue);
  const [open, setOpen] = useState(false);

  const applyNewValues = (newValues: string[]) => {
    const uniqueValues = [...new Set(newValues)];
    onChange?.(uniqueValues);
    if (column) column.setFilterValue(uniqueValues.length ? uniqueValues : undefined);
  };

  const toggleOptions = (option: string) => {
    const newValues = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];
    applyNewValues(newValues);
  };

  const clearFilters = () => {
    applyNewValues([]);
    setOpen(false);
    onClear?.();
  };

  const badgeText =
    getBadgeLabel?.(selectedValues) ?? (selectedValues.length > 0 ? String(selectedValues.length) : null);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild className="w-full">
        <div className="w-full">
          <Button
            variant="outline"
            className={`relative w-full cursor-pointer justify-between ${popoverFilter ? '' : 'lg:w-auto lg:justify-start lg:pr-8'}`}
          >
            <span className="flex items-center">
              {title}
              {badgeText && (
                <Chip className="bg-primary-700 absolute -top-3 -right-1 h-5 rounded-full py-2 text-[11px] leading-6 text-white">
                  {badgeText}
                </Chip>
              )}
            </span>
            <Icon name="chevron-down" className="ml-1 opacity-50" />
          </Button>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-54" side="bottom" align="start">
        {children ? (
          <div className="flex flex-col gap-3">{children}</div>
        ) : (
          <>
            {options?.map(({ label, value }: { label: string; value: string }) => (
              <DropdownMenuCheckboxItem
                key={value}
                checked={selectedValues.includes(value)}
                onCheckedChange={() => toggleOptions(value)}
                className="cursor-pointer"
              >
                {label}
              </DropdownMenuCheckboxItem>
            ))}
            {selectedValues.length > 0 && open && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearFilters();
                  }}
                  className="cursor-pointer pl-7"
                >
                  Clear filters
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
